"use client";

import {
  Activity,
  Brain,
  Calendar,
  CheckCircle2,
  Loader2,
  Mail,
  Megaphone,
  Plug,
  ServerCog,
  TrendingUp,
} from "lucide-react";
import { type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Reveal } from "@/components/ui/reveal";
import { SectionHeader } from "@/components/ui/section-header";

interface ChecklistItem {
  label: string;
  status: "done" | "processing";
}

interface BuildCard {
  icon: LucideIcon;
  label: string;
  sub: string;
}

interface ResultCard {
  icon: LucideIcon;
  num: string;
  label: string;
}

interface StepData {
  number: string;
  title: string;
  badge: string;
  description: string;
}

const STEPS: StepData[] = [
  {
    number: "01",
    title: "Tell Us About Your Business",
    badge: "Free audit call",
    description:
      "Send us a short inquiry and we set up a free audit call. We ask detailed questions about your business, your workflows, your tools, your pain points, and your goals. The more we learn upfront, the smarter your agents are from day one.",
  },
  {
    number: "02",
    title: "We Build Your Mission Control",
    badge: "Done for you",
    description:
      "Your audit becomes a custom proposal. Once you sign, your Raileon team sets up your private command center — one dashboard to manage every agent, every workflow, and every result. Connected to the tools you choose, running in a dedicated, isolated instance.",
  },
  {
    number: "03",
    title: "Your Agents Go to Work",
    badge: "24/7",
    description:
      "Log into your dashboard and watch your AI agents handle tasks around the clock. Marketing drafts, outreach, CRM updates — with anything that reaches a customer held for your approval.",
  },
];

const CHECKLIST: ChecklistItem[] = [
  { label: "Business model & revenue streams", status: "done" },
  { label: "Daily workflows & bottlenecks", status: "done" },
  { label: "Tools, platforms & integrations", status: "done" },
  { label: "Goals, KPIs & priorities", status: "done" },
  { label: "Digging into your workflows and tools...", status: "processing" },
];

const BUILD_CARDS: BuildCard[] = [
  { icon: ServerCog, label: "Mission Control", sub: "Your central dashboard" },
  { icon: Plug, label: "Apps Connected", sub: "Gmail, HubSpot, Slack + more" },
  { icon: Brain, label: "Agents Deployed", sub: "Configured with your business" },
  { icon: Activity, label: "Live Monitoring", sub: "Real-time activity feed" },
];

const RESULT_CARDS: ResultCard[] = [
  { icon: Mail, num: "47", label: "Follow-ups sent\n38% open rate" },
  { icon: Megaphone, num: "5", label: "Social posts\ndrafted for you" },
  { icon: CheckCircle2, num: "23", label: "Replies drafted\nfor your approval" },
  { icon: Calendar, num: "3", label: "Demos booked\nfor your approval" },
];

function Step1Card() {
  return (
    <div className="surface-card p-5">
      <div className="flex flex-col gap-2">
        {CHECKLIST.map((item) => (
          <div
            key={item.label}
            className="border-rl-border bg-rl-surface flex items-center justify-between rounded-lg border px-3 py-2.5"
          >
            <div className="flex items-center gap-2">
              {item.status === "done" ? (
                <CheckCircle2
                  size={13}
                  className="text-rl-accent shrink-0"
                  aria-hidden
                />
              ) : (
                <Loader2
                  size={13}
                  className="shrink-0 animate-spin text-cyan-400"
                  aria-hidden
                />
              )}
              <span className="text-rl-text2 text-xs font-medium">
                {item.label}
              </span>
            </div>
            <span
              className={cn(
                "rounded px-2 py-0.5 text-[9px] font-bold tracking-wide uppercase",
                item.status === "done"
                  ? "bg-rl-accent-soft text-rl-accent"
                  : "bg-cyan-400/10 text-cyan-400",
              )}
            >
              {item.status === "done" ? "Answered" : "Processing"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Step2Card() {
  return (
    <div className="surface-card p-5">
      <div className="grid grid-cols-2 gap-2.5">
        {BUILD_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className="border-rl-border bg-rl-surface rounded-lg border p-3"
            >
              <Icon
                size={16}
                className="text-rl-accent mb-2"
                aria-hidden
              />
              <div className="text-rl-text text-[11px] font-bold">
                {card.label}
              </div>
              <div className="text-rl-text3 text-[10px]">{card.sub}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-4">
        <div className="mb-1.5 flex justify-between text-[10px] font-semibold">
          <span className="text-rl-text2">Workspace setup</span>
          <span className="text-rl-accent font-mono">72%</span>
        </div>
        <div className="bg-rl-surface2 h-[6px] overflow-hidden rounded-full">
          <div
            className="from-rl-accent h-full rounded-full bg-gradient-to-r to-cyan-400"
            style={{ width: "72%" }}
          />
        </div>
      </div>
    </div>
  );
}

function Step3Card() {
  return (
    <div className="surface-card p-5">
      <div className="grid grid-cols-2 gap-2.5">
        {RESULT_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className="border-rl-border bg-rl-surface flex items-center gap-3 rounded-lg border p-3"
            >
              <Icon size={18} className="text-rl-accent" aria-hidden />
              <div>
                <div className="text-rl-accent font-mono text-[20px] leading-none font-black">
                  {card.num}
                </div>
                <div className="text-rl-text2 mt-1 text-[10px] leading-tight whitespace-pre-line">
                  {card.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-rl-accent/15 bg-rl-accent/5 mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-2">
        <TrendingUp
          size={12}
          className="text-rl-accent shrink-0"
          aria-hidden
        />
        <span className="text-rl-accent text-[10px] font-semibold">
          Agents running now{" "}
          <span className="text-rl-text2 font-normal">
            (last task 4 min ago)
          </span>
        </span>
      </div>
    </div>
  );
}

const STEP_VISUALS = [Step1Card, Step2Card, Step3Card];

const HOW_IT_WORKS_HEADING = (
  <>
    Three steps.
    <br />
    We handle the setup.
  </>
);

export function HowItWorks() {
  return (
    <section id="how" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-280 px-6 md:px-8">
        <SectionHeader
          tag="How It Works"
          heading={HOW_IT_WORKS_HEADING}
          subheading="No coding required. You connect your own accounts — we walk you through every step."
        />

        <div className="mx-auto max-w-6xl">
          {STEPS.map((step, i) => {
            const Visual = STEP_VISUALS[i]!;
            const flipped = i % 2 === 1;
            return (
              <div
                key={step.number}
                className={cn(
                  "border-rl-border grid items-center gap-8 border-t py-14 first:border-t-0 first:pt-2 last:pb-2 md:grid-cols-2 md:gap-16",
                )}
              >
                <Reveal delay={50}>
                  <div className={cn(flipped && "md:order-2")}>
                    <div className="mb-5 flex items-center gap-3">
                      <span className="text-rl-accent border-rl-accent/30 bg-rl-accent-soft inline-flex size-12 items-center justify-center rounded-full border font-mono text-[15px] font-extrabold">
                        {step.number}
                      </span>
                      <span className="border-rl-border bg-rl-bg3 text-rl-text2 inline-flex items-center rounded-md border px-2.5 py-1 font-mono text-[11px] font-semibold">
                        {step.badge}
                      </span>
                    </div>
                    <h3 className="text-foreground mb-3 text-[1.5rem] leading-[1.2] font-extrabold tracking-tight md:text-[1.75rem]">
                      {step.title}
                    </h3>
                    <p className="text-rl-text2 max-w-md text-[0.9375rem] leading-[1.7]">
                      {step.description}
                    </p>
                  </div>
                </Reveal>

                <Reveal delay={120}>
                  <div className={cn(flipped && "md:order-1")}>
                    <Visual />
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
